import React from 'react';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import SkipButton from './SkipButton';
import useStyles from './NewTermsForm.styles';
import influencerActions from '../../../actions/influencer.actions';
import {useGetMessages, useFormEnableSubmit} from '../../../hooks';
import {SubmitHandler} from 'react-hook-form';
import {getDateDiff, TimeInSeconds} from '../../../../shared/utils/DateUtils';
import {useDispatch, useSelector} from 'react-redux';
import {selectAffiliateLoading} from '../../../selectors';
import {Eula} from '../../../reducers/eula.reducer';
import {Button, HelpLinks} from '../../../components';

import {
    withRHFormProvider,
    WithRHFormProviderProps,
    withTalon,
    WithTalonProps
} from '../../../hocs';

import {
    Guardian,
    GuardianFieldValues,
    LegalAgeAcknowledge,
    LegalAgeAcknowledgeFieldValues
} from './form-sections';

interface NewTermsFormProps {
    className?: string;
    isRendering?: boolean;
    agreement: Eula;
    migrateBy: string;
    scrolledToBottom?: boolean;
}

type NewTermsFormFieldValues = LegalAgeAcknowledgeFieldValues & Partial<GuardianFieldValues>;

const NewTermsForm: React.FC<
    NewTermsFormProps & WithRHFormProviderProps<NewTermsFormFieldValues> & WithTalonProps
> = ({methods, className, isRendering, agreement, migrateBy, scrolledToBottom = false, talon}) => {
    const legalAgeAcknowledge = methods.watch('legalAgeAcknowledge');
    const enableSubmitButton = useFormEnableSubmit();
    const dispatch = useDispatch();
    const affiliateLoading = useSelector(selectAffiliateLoading);
    const styles = useStyles();
    const messages = useGetMessages(() => {
        const remainingDays = getDateDiff(Date.now(), migrateBy, TimeInSeconds.DAY);
        return {
            acceptTooltip: 'epic.affiliate.tos.acceptTooltip.v2',
            accept: 'epic.affiliate.migration.signup.accept.v2',
            reject: 'epic.affiliate.migration.signup.noAccept.v2',
            skip: 'epic.affiliate.common.skip',
            skipModalBody: {
                code: 'epic.affiliate.tos.warningNote.modal.v2',
                args: [remainingDays > 0 ? remainingDays : 0]
            },
            required: 'errors.com.epicgames.validation.required_field.v2'
        };
    }, [migrateBy]);

    const onSubmit: SubmitHandler<NewTermsFormFieldValues> = data => {
        talon.initCaptcha(solveToken => {
            const {legalAgeAcknowledge, firstName, lastName, relationship} = data || {};
            dispatch(
                influencerActions.acceptAgreement({
                    age18Acknowledgment: legalAgeAcknowledge === 'adult',
                    guardianAcknowledgment: legalAgeAcknowledge === 'adultGuardian',
                    agreementAccepted: true, // if submitted = user clicked 'Accept'
                    eulas: [
                        {
                            key: agreement.key,
                            version: agreement.version,
                            locale: agreement.locale
                        }
                    ],
                    guardianInfo: {
                        firstName,
                        lastName,
                        relationship
                    },
                    solveToken
                })
            );
        });
    };

    const {formState} = methods;
    const isLoading = formState.isSubmitting || affiliateLoading || talon.talonState.loading;
    const enableButton = scrolledToBottom && !isLoading && enableSubmitButton;

    return (
        <form className={className} autoComplete="off" onSubmit={methods.handleSubmit(onSubmit)}>
            <Box className={styles.formBody}>
                <LegalAgeAcknowledge className={styles.legalAgeSection} isLoading={isRendering} />
                {legalAgeAcknowledge === 'adultGuardian' && <Guardian />}
                <Box className={styles.submitSection}>
                    <Tooltip
                        classes={{tooltip: styles.acceptTooltip}}
                        title={messages.acceptTooltip}
                        interactive
                        placement="top"
                        leaveDelay={200}>
                        <span>
                            <Button
                                type="submit"
                                color="primary"
                                size="large"
                                loading={isLoading}
                                disabled={!enableButton}
                                isLoading={isRendering}
                                fullWidth>
                                {messages.accept}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button color="error" size="large" to="/close-account" isLoading={isRendering}>
                        {messages.reject}
                    </Button>
                    <SkipButton variant="text" label={messages.skip}>
                        {messages.skipModalBody}
                    </SkipButton>
                </Box>
                <HelpLinks isLoading={isRendering} />
            </Box>
        </form>
    );
};

const EnhancedNewTermsForm = withRHFormProvider<NewTermsFormProps, NewTermsFormFieldValues>(
    withTalon(NewTermsForm),
    {
        mode: 'onChange',
        shouldUnregister: true
    }
);

export default EnhancedNewTermsForm;
